/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useState } from "react";
import AccountInfoEnable from "../components/my-account/editAccount-enable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EditAccountPasswordVerify from "../components/my-account/editAccount-passwordVerify";
import EditAccountOtp from "../components/my-account/editAccount-otp";
import type { IUpdateAccountInfoPayload } from "@/hooks/use-update-accountInfo";
import type {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";

export enum EditAccountViewState {
  ENABLE = "enable",
  SEND_OTP = "sendOtp",
  VERIFY_PASSWORD = "verifyPassword",
}

type EditAccountContextType = {
  viewState: EditAccountViewState;
  setViewState: (viewState: EditAccountViewState) => void;
  onOpenChange: (isOpen: boolean) => void;
  payloadData: IUpdateAccountInfoPayload;
  setPayloadData: (data: IUpdateAccountInfoPayload) => void;
};

export const EditAccountContext = createContext<EditAccountContextType>({
  viewState: EditAccountViewState.ENABLE,
  setViewState: () => {},
  onOpenChange: () => {},
  payloadData: {},
  setPayloadData: () => {},
});

const EditAccountContainer = ({
  open,
  onOpenChange,
  logDialogName,
  logTrigger,
  logTag,
  logCategory,
  logSubCategory,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  logDialogName?: string;
  logTrigger?: string;
  logTag: LogTags;
  logCategory: LogCategories;
  logSubCategory: LogSubCategories;
}) => {
  const [viewState, setViewState] = useState(EditAccountViewState.ENABLE);
  const [payloadData, setPayloadData] = useState<IUpdateAccountInfoPayload>(
    {} as IUpdateAccountInfoPayload,
  );

  const handleOpenChangeReset = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setViewState(EditAccountViewState.ENABLE);
        setPayloadData({});
      }
      onOpenChange(isOpen);
    },
    [onOpenChange],
  );

  const renderContent = () => {
    switch (viewState) {
      case EditAccountViewState.ENABLE:
        return <AccountInfoEnable />;
      case EditAccountViewState.VERIFY_PASSWORD:
        return <EditAccountPasswordVerify />;
      case EditAccountViewState.SEND_OTP:
        return <EditAccountOtp />;
      default:
        onOpenChange(false);
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChangeReset}
      logDialogName={logDialogName}
      logTrigger={logTrigger}
      logTag={logTag}
      logCategory={logCategory}
      logSubCategory={logSubCategory}
    >
      <DialogContent
        className="flex max-h-[min(90dvh,100%)] min-h-0 w-[calc(100vw-1.5rem)] max-w-xl flex-col gap-0 overflow-hidden p-4 sm:w-full sm:p-6"
        logDialogName={logDialogName}
      >
        <DialogHeader className="flex-shrink-0 pb-3 sm:pb-4">
          {viewState !== "enable" ? (
            <DialogTitle className="flex flex-col items-start gap-1 pr-8 text-base font-medium sm:text-lg">
              Update Account Details
            </DialogTitle>
          ) : (
            ""
          )}
        </DialogHeader>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch overflow-hidden px-2">
          <EditAccountContext.Provider
            value={{
              viewState,
              setViewState,
              onOpenChange: handleOpenChangeReset,
              payloadData,
              setPayloadData,
            }}
          >
            {renderContent()}
          </EditAccountContext.Provider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditAccountContainer;
